import { JSONSchema7WithErrors } from '@cibo/core'
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from '@mui/material'
import { connectField } from 'dynamic-uniforms'
import { Markdown } from '../../Markdown'
import { useCiboAutoForm } from './CiboAutoFormContext'
import { ClearButton } from './ClearButton'
import { ValidationMessages } from './ValidationMessages'

type CheckboxOptionsInputProps = {
  name: string
  value: string[]
  label?: string
  onChange: (value: string[]) => void
  disabled?: boolean
  error?: { message: string }
  required: boolean
  items: JSONSchema7WithErrors
  type: string
  field: { type: string }
  [key: string]: any
}

export function CheckboxOptionsInput(props: CheckboxOptionsInputProps) {
  const { name, value, label, onChange, disabled, required } = props
  const { touch } = useCiboAutoForm()
  const canClear = !disabled && value && value.length > 0

  if (props.field?.type !== 'array') {
    throw new Error('CheckboxOptionsInput only works with type:"array"')
  }

  if (typeof props.items.anyOf === 'undefined') {
    throw new Error('CheckboxOptionsInput only works with items.anyOf')
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const options = props.items.anyOf!.map((option: any) =>
    typeof option === 'object'
      ? { value: option.const, label: option.label }
      : { value: option, label: option }
  )

  const selected = value ?? []

  const handleChange = (optionValue: string, checked: boolean) => {
    const next = checked
      ? [...selected, optionValue]
      : selected.filter((v: string) => v !== optionValue)
    onChange(next)
    touch(name)
  }

  return (
    <FormControl fullWidth required={required} disabled={disabled}>
      {label && (
        <FormLabel component="legend">
          <Markdown>{label}</Markdown>
        </FormLabel>
      )}

      <FormGroup row>
        {options.map((option: any) => (
          <FormControlLabel
            key={option.value}
            label={option.label}
            disabled={disabled}
            control={
              <Checkbox
                checked={selected.includes(option.value)}
                onChange={e => handleChange(option.value, e.target.checked)}
                onBlur={() => touch(name)}
              />
            }
          />
        ))}
        {canClear && (
          <ClearButton
            tooltipKey="clearButton.selection"
            onClick={() => {
              onChange([])
              touch(name)
            }}
            sx={{ ml: 1 }}
          />
        )}
      </FormGroup>

      <ValidationMessages
        fieldName={props.name}
        error={props.error as { message: string } | undefined}
      />
    </FormControl>
  )
}

export default connectField(CheckboxOptionsInput)
